import React, { useState, useEffect, useCallback, useRef } from "react";
import moment from "moment-timezone";
import "daterangepicker";
import $ from "jquery";
import * as XLSX from "xlsx";
import LeftMenuList from "../Common/LeftMenuList";
import TopNavbar from "../Common/TopNavbar";
import Loader from "../utils/Loader";
import NewReports from "./Report/EnterpriseData";
import { TEST_DATA_RECORDS } from "../api/api";

const DATE_FORMAT = "M/DD/YYYY hh:mm:ss A";

const flattenData = (obj, prefix = "") => {
  return Object.entries(obj || {}).reduce((acc, [key, value]) => {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      Object.assign(acc, flattenData(value, fullKey));
    } else {
      acc[fullKey] = value;
    }
    return acc;
  }, {});
};

const formatIST = (value, isEpochSeconds) => {
  const m = isEpochSeconds ? moment.unix(value) : moment(value);
  return m.tz("Asia/Kolkata").format("DD-MM-YYYY hh:mm:ss A");
};

const getDisplayTimestamp = (record) => {
  const raw = record?.data?.timestamp;
  const num = typeof raw === "number" ? raw : parseInt(raw, 10);
  if (!isNaN(num) && num > 0) {
    return formatIST(num, true);
  }
  return formatIST(record.createdAt, false);
};

const PAGE_SIZE = 100;
const EXPORT_PAGE_SIZE = 500;
const MAX_EXPORT_ROWS = 50000;

const InverterACDevice = () => {
  const [selectedEnterprise, setSelectedEnterprise] = useState("");
  const [selectedCountryState, setSelectedCountryState] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedGateway, setSelectedGateway] = useState("");
  const [selectedOptimizer, setSelectedOptimizer] = useState("");
  const [gatewayData, setGatewayData] = useState(null);
  const [optimizerData, setOptimizerData] = useState(null);
  const selectedDevice = optimizerData?.nameId || "";

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const dateRangePickerRef = useRef(null);

  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (dateRangePickerRef.current) {
        const startDateToUse = moment().startOf("day");
        const endDateToUse = moment(startDateToUse).endOf("day");

        $(dateRangePickerRef.current).daterangepicker({
          timePicker: true,
          startDate: startDateToUse,
          endDate: endDateToUse,
          locale: {
            format: "M/DD/YYYY hh:mm A",
          },
        });

        $(dateRangePickerRef.current).on("apply.daterangepicker", (ev, picker) => {
          setFromDate(picker.startDate.format(DATE_FORMAT));
          setToDate(picker.endDate.format(DATE_FORMAT));
        });
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if ($(dateRangePickerRef.current).data("daterangepicker")) {
        $(dateRangePickerRef.current).data("daterangepicker").remove();
      }
    };
  }, []);

  const loadRecords = useCallback(
    async (pageNum) => {
      setLoading(true);
      setError("");
      try {
        const params = { page: pageNum, limit: PAGE_SIZE };
        if (selectedDevice) params.device_id = selectedDevice;
        if (fromDate) params.from = moment(fromDate, DATE_FORMAT).toISOString();
        if (toDate) params.to = moment(toDate, DATE_FORMAT).toISOString();

        const res = await TEST_DATA_RECORDS(params);
        setRecords(res?.data?.records || []);
        setPage(res?.data?.page || 1);
        setTotalPages(res?.data?.totalPages || 1);
        setTotal(res?.data?.total || 0);
      } catch (err) {
        setError(err?.response?.data?.message || "Failed to load data.");
        setRecords([]);
      } finally {
        setLoading(false);
      }
    },
    [selectedDevice, fromDate, toDate]
  );

  useEffect(() => {
    loadRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoadClick = () => loadRecords(1);
  const handlePrev = () => {
    if (page > 1) loadRecords(page - 1);
  };
  const handleNext = () => {
    if (page < totalPages) loadRecords(page + 1);
  };

  const flattenedRows = records.map((record) => ({
    _id: record._id,
    __timestamp: getDisplayTimestamp(record),
    ...flattenData(record.data),
  }));

  const columns = [];
  const seen = new Set();
  flattenedRows.forEach((row) => {
    Object.keys(row).forEach((key) => {
      if (key !== "_id" && key !== "__timestamp" && !seen.has(key)) {
        seen.add(key);
        columns.push(key);
      }
    });
  });

  const handleDownloadExcel = async () => {
    setDownloading(true);
    setDownloadProgress(null);
    setError("");

    try {
      const baseParams = {};
      if (selectedDevice) baseParams.device_id = selectedDevice;
      if (fromDate) baseParams.from = moment(fromDate, DATE_FORMAT).toISOString();
      if (toDate) baseParams.to = moment(toDate, DATE_FORMAT).toISOString();

      const firstRes = await TEST_DATA_RECORDS({ ...baseParams, page: 1, limit: EXPORT_PAGE_SIZE });
      const grandTotal = firstRes?.data?.total || 0;
      const pagesToFetch = firstRes?.data?.totalPages || 1;

      if (grandTotal > MAX_EXPORT_ROWS) {
        setError(
          `The selected range has ${grandTotal} records, which exceeds the export limit of ${MAX_EXPORT_ROWS}. Narrow the device or date range and try again.`
        );
        return;
      }

      let allRecords = firstRes?.data?.records || [];
      setDownloadProgress({ page: 1, totalPages: pagesToFetch });

      for (let p = 2; p <= pagesToFetch; p++) {
        const res = await TEST_DATA_RECORDS({ ...baseParams, page: p, limit: EXPORT_PAGE_SIZE });
        allRecords = allRecords.concat(res?.data?.records || []);
        setDownloadProgress({ page: p, totalPages: pagesToFetch });
      }

      if (!allRecords.length) {
        setError("No data available to download for the selected filters.");
        return;
      }

      const allFlattenedRows = allRecords.map((record) => ({
        _id: record._id,
        __timestamp: getDisplayTimestamp(record),
        ...flattenData(record.data),
      }));

      const exportColumns = [];
      const exportSeen = new Set();
      allFlattenedRows.forEach((row) => {
        Object.keys(row).forEach((key) => {
          if (key !== "_id" && key !== "__timestamp" && !exportSeen.has(key)) {
            exportSeen.add(key);
            exportColumns.push(key);
          }
        });
      });

      const exportRows = allFlattenedRows.map((row) => {
        const exportRow = { "Timestamp (IST)": row.__timestamp };
        exportColumns.forEach((col) => {
          exportRow[col] = row[col] === undefined || row[col] === null ? "" : row[col];
        });
        return exportRow;
      });

      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Inverter AC Data");

      const filenameParts = ["InverterACDevice"];
      if (selectedDevice) filenameParts.push(selectedDevice);
      filenameParts.push(moment().tz("Asia/Kolkata").format("YYYYMMDD_HHmmss"));

      XLSX.writeFile(workbook, `${filenameParts.join("_")}.xlsx`);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to download data.");
    } finally {
      setDownloading(false);
      setDownloadProgress(null);
    }
  };

  return (
    <>
      {loading && <Loader />}
      <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
        <LeftMenuList />
        <div className="flex flex-col flex-1 w-full">
          <TopNavbar />
          <main className="h-full overflow-y-auto">
            <div className="container px-6 mx-auto grid">
              <h2 className="my-6 text-2xl font-semibold text-gray-700 dark:text-gray-200">
                Inverter AC Device
              </h2>

              <NewReports
                selectedEnterprise={selectedEnterprise}
                selectedCountryState={selectedCountryState}
                selectedLocation={selectedLocation}
                selectedGateway={selectedGateway}
                selectedOptimizer={selectedOptimizer}
                setSelectedEnterprise={setSelectedEnterprise}
                setSelectedLocation={setSelectedLocation}
                setSelectedCountryState={setSelectedCountryState}
                setSelectedGateway={setSelectedGateway}
                setSelectedOptimizer={setSelectedOptimizer}
                formDisabled={false}
                hideOptimizer={false}
                isOptimizerDisabled={false}
                selectedTab="inverterAcDevice"
                setGatewayData={setGatewayData}
                setOptimizerData={setOptimizerData}
              />

              <div className="flex flex-wrap items-end gap-4 mb-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
                    Date Range
                  </label>
                  <input
                    ref={dateRangePickerRef}
                    name="datetimes"
                    className="block w-64 text-sm dark:text-gray-300 dark:border-gray-600 dark:bg-gray-700 form-select focus:border-purple-400 focus:outline-none focus:shadow-outline-purple dark:focus:shadow-outline-gray"
                    placeholder="Select date range"
                  />
                </div>

                <button
                  type="button"
                  onClick={handleLoadClick}
                  className="py-2 px-4 text-sm font-medium leading-5 text-white transition-colors duration-150 bg-purple-600 border border-transparent rounded-lg active:bg-purple-600 hover:bg-purple-700 focus:outline-none focus:shadow-outline-purple"
                >
                  Load
                </button>

                <button
                  type="button"
                  onClick={handleDownloadExcel}
                  disabled={!total || downloading}
                  className={`py-2 px-4 text-sm font-medium leading-5 text-white transition-colors duration-150 border border-transparent rounded-lg focus:outline-none ${
                    total && !downloading
                      ? "bg-purple-600 hover:bg-purple-700 active:bg-purple-600 focus:shadow-outline-purple"
                      : "cursor-not-allowed"
                  }`}
                  style={
                    !(total && !downloading)
                      ? { backgroundColor: "#D1D5DB", color: "#9CA3AF" }
                      : undefined
                  }
                >
                  {downloading
                    ? `Downloading... (${downloadProgress?.page || 1}/${downloadProgress?.totalPages || 1})`
                    : "Download Excel"}
                </button>
              </div>

              {error && (
                <div className="mb-4 text-sm text-red-600">{error}</div>
              )}

              <div className="overflow-x-auto max-h-[28rem] border dark:border-gray-700 rounded-lg">
                <table className="w-full whitespace-no-wrap">
                  <thead>
                    <tr className="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase border-b dark:border-gray-700 bg-gray-50 dark:text-gray-400 dark:bg-gray-800">
                      <th className="px-4 py-3">Timestamp (IST)</th>
                      {columns.map((col) => (
                        <th className="px-4 py-3" key={col}>
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y dark:divide-gray-700 dark:bg-gray-800">
                    {flattenedRows.length === 0 && !loading && (
                      <tr>
                        <td
                          className="px-4 py-3 text-sm text-center text-gray-500"
                          colSpan={columns.length + 1}
                        >
                          No data found.
                        </td>
                      </tr>
                    )}
                    {flattenedRows.map((row) => (
                      <tr
                        className="text-gray-700 dark:text-gray-400"
                        key={row._id}
                      >
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          {row.__timestamp}
                        </td>
                        {columns.map((col) => (
                          <td className="px-4 py-3 text-sm" key={col}>
                            {row[col] === undefined || row[col] === null
                              ? ""
                              : String(row[col])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="grid px-4 py-3 text-xs font-semibold tracking-wide text-gray-500 uppercase border-t dark:border-gray-700 bg-gray-50 sm:grid-cols-9 dark:text-gray-400 dark:bg-gray-800">
                <span className="flex items-center col-span-3">
                  {`Page ${page} of ${totalPages} (${total} total)`}
                </span>
                <span className="col-span-2"></span>
                <span className="flex col-span-4 mt-2 sm:mt-auto sm:justify-end">
                  <nav aria-label="Table navigation">
                    <ul className="inline-flex items-center">
                      <li>
                        <button
                          className={`py-2 px-3 mt-2 mr-3 focus:outline-none text-white rounded-lg ${
                            page === 1
                              ? "cursor-not-allowed"
                              : "bg-purple-600 hover:bg-purple-700"
                          }`}
                          style={
                            page === 1
                              ? { backgroundColor: "#D1D5DB", color: "#9CA3AF" }
                              : undefined
                          }
                          aria-label="Previous"
                          onClick={handlePrev}
                          disabled={page === 1}
                        >
                          Prev
                        </button>
                      </li>
                      <li>
                        <button
                          className={`py-2 px-3 mt-2 focus:outline-none text-white rounded-lg ${
                            page >= totalPages
                              ? "cursor-not-allowed"
                              : "bg-purple-600 hover:bg-purple-700"
                          }`}
                          style={
                            page >= totalPages
                              ? { backgroundColor: "#D1D5DB", color: "#9CA3AF" }
                              : undefined
                          }
                          aria-label="Next"
                          onClick={handleNext}
                          disabled={page >= totalPages}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </span>
              </div>
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export default InverterACDevice;
